import React from "react";
import { Button, Popover, Tag, Tooltip, App } from "antd";
import { useRouter } from "next/router";
import { createUseStyles } from "react-jss";
import {
  faEnvelope,
  faFileCircleCheck,
} from "@fortawesome/pro-duotone-svg-icons";

import Icon from "../Icon";
import { pascalCase } from "../common/PascalCase";
import { UIAvatar, UIHeading, UIText } from "../ui/UIElements";

const RecentCandidateCard = ({ data }) => {
  const styles = useStyles();
  const router = useRouter();
  const { message } = App.useApp();

  const recentJob = data?.DataFields || {};
  const jobs = data?.Jobs || [];

  const candidateName = pascalCase(
    `${recentJob?.FirstName || ""} ${recentJob?.LastName || ""}`
  );

  const goToCandidate = (item = recentJob) => {
    router.push({
      pathname: `/employer/candidates/${item?.MemberJobHistorySlug}`,
      query: {
        q: "Profile",
        pg: 1,
      },
    });
  };

  const getStatusStyle = (status) => {
    const styles = {
      New: {
        background: "#eff6ff",
        color: "#2364ae",
      },
      "In Review": {
        background: "#fffbeb",
        color: "#d97706",
      },
      Rejected: {
        background: "#fef2f2",
        color: "#dc2626",
      },
      Interview: {
        background: "#ecfeff",
        color: "#0891b2",
      },
      Offered: {
        background: "#fefce8",
        color: "#ca8a04",
      },
      Hired: {
        background: "#ecfdf3",
        color: "#16a34a",
      },
    };

    return (
      styles[status] || {
        background: "#f1f5f9",
        color: "#475569",
      }
    );
  };

  const AdditionalJobs = (
    <div className={styles.popoverContent}>
      {jobs.map((item, index) => (
        <div
          key={index}
          className={styles.popoverJob}
          onClick={(e) => {
            e.stopPropagation();
            goToCandidate(item);
          }}
        >
          <div className={styles.popoverJobTitle}>{item?.JobTitle}</div>

          <Tag
            style={{
              border: "none",
              borderRadius: 999,
              fontWeight: 700,
              margin: 0,
              ...getStatusStyle(item?.StatusDetail),
            }}
          >
            {item?.StatusDetail}
          </Tag>
        </div>
      ))}
    </div>
  );

  const copyEmail = (e) => {
    e.stopPropagation();

    if (!recentJob?.EmailAddress) return;

    navigator?.clipboard?.writeText(recentJob?.EmailAddress);
    message.success("Copied to clipboard");
  };

  return (
    <div className={styles.card} onClick={() => goToCandidate()}>
      <div className={styles.cardLeft}>
        <UIAvatar
          size={52}
          src={recentJob?.ProfilePhoto}
          name={candidateName}
        />

        <div className={styles.cardContent}>
          <button className={styles.titleButton} onClick={() => goToCandidate()}>
            <UIHeading level={5}>{candidateName}</UIHeading>
          </button>

          <div className={styles.tagRow}>
            {jobs.length === 1 && recentJob?.StatusDetail && (
              <Tooltip placement="top" title="Status">
                <Tag
                  className={styles.statusTag}
                  style={getStatusStyle(recentJob?.StatusDetail)}
                  onClick={(e) => e.stopPropagation()}
                >
                  {recentJob?.StatusDetail}
                </Tag>
              </Tooltip>
            )}

            {jobs.length > 1 && (
              <Tooltip placement="top" title="Status">
                <Popover
                  placement="topRight"
                  title="Applications"
                  content={AdditionalJobs}
                  trigger="click"
                >
                  <Tag
                    className={styles.statusTag}
                    style={{
                      background: "#eff6ff",
                      color: "#2364ae",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Multiple Applications
                  </Tag>
                </Popover>
              </Tooltip>
            )}

            {recentJob?.AppliedOn && (
              <Tooltip placement="top" title="Applied On">
                <Tag
                  className={styles.appliedTag}
                  onClick={(e) => e.stopPropagation()}
                >
                  {recentJob?.AppliedOn} ago
                </Tag>
              </Tooltip>
            )}
          </div>

          {recentJob?.JobTitle && (
            <Tooltip placement="top" title="Latest Application">
              <div className={styles.infoRow}>
                <Icon
                  size={18}
                  style={{ marginRight: 8, color: "#94a3b8" }}
                  Icon={faFileCircleCheck}
                />

                <UIText type="secondary" className={styles.infoText}>
                  {recentJob?.JobTitle}
                </UIText>
              </div>
            </Tooltip>
          )}

          {recentJob?.EmailAddress && (
            <Tooltip placement="top" title="Double click to copy email">
              <div
                className={styles.infoRow}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={copyEmail}
              >
                <Icon
                  size={18}
                  style={{ marginRight: 8, color: "#94a3b8" }}
                  Icon={faEnvelope}
                />

                <UIText type="secondary" className={styles.emailText}>
                  {recentJob?.EmailAddress}
                </UIText>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecentCandidateCard;

const useStyles = createUseStyles({
card: {
  border: "1px solid #e5e7eb",
  borderRadius: 20,
  padding: 18,
  background: "#ffffff",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 16,
  cursor: "pointer",
  transition: "all 0.22s ease",
  marginTop: 15,

  "&:hover": {
    borderColor: "#dbeafe",
    background: "#fcfdff",
    boxShadow: "0 10px 24px rgba(37,99,235,0.08)",
    transform: "translateY(-1px)",
  },

  "@media (max-width: 640px)": {
    alignItems: "flex-start",
    padding: 16,
  },
},

  cardLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    flex: 1,
    minWidth: 0,
    width: "100%",
  },

  cardContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },

  titleButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    textAlign: "left",
    cursor: "pointer",
    fontSize: 16,
    fontWeight: 700,
    color: "#0f172a",
    width: "fit-content",

    "&:hover": {
      color: "#2364ae",
    },
  },

  tagRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",

  },

  statusTag: {
    width: "fit-content",
    borderRadius: 999,
    border: "none",
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 700,
    margin: 0,
  },

  appliedTag: {
    width: "fit-content",
    borderRadius: 999,
    border: "none",
    padding: "4px 10px",
    fontSize: 12,
    fontWeight: 700,
    margin: 0,
    background: "#f1f5f9",
    color: "#475569",
  },

  infoRow: {
    display: "flex",
    alignItems: "center",
    marginTop: 8,
    minWidth: 0,
    color: "#475569",
  },

  infoText: {
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  emailText: {
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },


  popoverContent: {
    width: 280,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  popoverJob: {
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    transition: "all 0.2s ease",

    "&:hover": {
      background: "#f8fafc",
      borderColor: "#dbeafe",
    },
  },

  popoverJobTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#0f172a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});